import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { getTenantSlug } from '../helpers/hostname-utils';
import { RemoteStorageContent, RemoteStoragePayload, RemoteStorageQuery } from '../models/RemoteStorage';
import { EnvironmentService } from './environment.service';

const REMOTE_STORAGE_URI = '/storage/remote/items';

/**
 * Remote storage stores data for a tenant and space with key tenant_space
 * Data is stored in the form of an object referred to as 'propertyBag' of type RemoteStorageContent
 * E.g.
 * {
 *  lastVisitedSpace: 'space1' // an item
 * }
 * Each key inside the propertyBag is referred to as 'item' of type RemoteStorageValue
 */
@Injectable({
  providedIn: 'root'
})
export class RemoteStorageV2Service {
  private readonly restUrl: string;
  private readonly remoteStorageCache: { [key: string]: Subject<RemoteStorageContent> } = {};

  constructor(private readonly environmentService: EnvironmentService, private readonly httpClient: HttpClient) {
    this.restUrl = this.environmentService.getRestUrl(REMOTE_STORAGE_URI);
  }

  /**
   * Get stream of propertyBag for specific key, tenant and space
   *
   * @param tenantUri
   * @param tenantSlug
   * @param spaceUri
   */
  public get(tenantUri?: string, spaceUri?: string, tenantSlug?: string): Observable<RemoteStorageContent> {
    const remoteDataKey = this.getRemoteStorageKey(tenantUri, spaceUri, tenantSlug);

    if (this.remoteStorageCache[remoteDataKey]) {
      return this.remoteStorageCache[remoteDataKey].asObservable();
    }

    this.remoteStorageCache[remoteDataKey] = new ReplaySubject<RemoteStorageContent>(1);
    const params = this.generateQueryParams(tenantUri, spaceUri, tenantSlug);
    this.httpClient
      .get<RemoteStorageContent>(`${this.restUrl}`, { params })
      .pipe(
        take(1),
        catchError((error) => this.handleErrors(error))
      )
      .subscribe((storageData) => this.remoteStorageCache[remoteDataKey].next(storageData));

    return this.remoteStorageCache[remoteDataKey].asObservable();
  }

  /**
   * Create a new item or update if it present and update the cache
   *
   * @param key
   * @param tenantUri
   * @param spaceUri
   * @param value
   */
  public set(
    key: string,
    value: RemoteStoragePayload,
    tenantUri?: string,
    spaceUri?: string,
    tenantSlug?: string
  ): Observable<RemoteStorageContent> {
    const remoteDataKey = this.getRemoteStorageKey(tenantUri, spaceUri, tenantSlug);

    if (!this.remoteStorageCache[remoteDataKey]) {
      this.remoteStorageCache[remoteDataKey] = new ReplaySubject<RemoteStorageContent>(1);
    }

    const params = this.generateQueryParams(tenantUri, spaceUri, tenantSlug);
    return this.httpClient.post<RemoteStorageContent>(`${this.restUrl}/${key}`, { value }, { params }).pipe(
      catchError((error) => this.handleErrors(error)),
      tap((storageData) => {
        this.remoteStorageCache[remoteDataKey].next(storageData);
      })
    );
  }

  /**
   * Unset/Delete an item for specific key, tenant and space
   *
   * @param key
   * @param tenantUri
   * @param spaceUri
   */
  public unset(key: string, tenantUri?: string, spaceUri?: string, tenantSlug?: string): Observable<RemoteStorageContent> {
    const remoteDataKey = this.getRemoteStorageKey(tenantUri, spaceUri, tenantSlug);

    if (!this.remoteStorageCache[remoteDataKey]) {
      this.remoteStorageCache[remoteDataKey] = new ReplaySubject<RemoteStorageContent>(1);
    }

    const params = this.generateQueryParams(tenantUri, spaceUri, tenantSlug);
    return this.httpClient.delete<RemoteStorageContent>(`${this.restUrl}/${key}`, { params }).pipe(
      take(1),
      catchError((error) => this.handleErrors(error)),
      tap((storageData) => {
        this.remoteStorageCache[remoteDataKey].next(storageData);
      })
    );
  }

  /**
   * Delete all the items for a specific tenant and space
   *
   * @param tenantUri
   * @param spaceUri
   */
  public clear(tenantUri?: string, spaceUri?: string): Observable<void> {
    const remoteDataKey = this.getRemoteStorageKey(tenantUri, spaceUri);

    const params = this.generateQueryParams(tenantUri, spaceUri);
    return this.httpClient.delete<void>(`${this.restUrl}`, { params }).pipe(
      take(1),
      catchError((error) => this.handleErrors(error)),
      tap(() => {
        if (this.remoteStorageCache[remoteDataKey]) {
          this.remoteStorageCache[remoteDataKey].next(null);
        }
      })
    );
  }

  private handleErrors(errorResponse: HttpErrorResponse) {
    if (errorResponse?.error?.error === 'NotFoundError') {
      return of(null);
    }
    return throwError(errorResponse);
  }

  /**
   * Generates params for endpoint requests
   *
   * @param tenantUri
   * @param spaceUri
   * @param tenantSlug
   * @param query
   * @returns
   */
  private generateQueryParams(tenantUri?: string, spaceUri?: string, tenantSlug?: string, query?: RemoteStorageQuery) {
    let params = {};
    if (tenantUri) {
      params['tenantUri'] = tenantUri;
    }
    if (spaceUri) {
      params['spaceUri'] = spaceUri;
    }
    if (tenantSlug) {
      params['tenantSlug'] = tenantSlug;
    }
    if (query) {
      params = { ...params, ...query };
    }
    return params;
  }

  private getRemoteStorageKey(tenantUri?: string, spaceUri?: string, tenantSlug?: string): string {
    // If only tenantUri is provided then fetch the tenant slug instead
    if (tenantUri && !tenantSlug) {
      tenantSlug = getTenantSlug();
    }
    return `${tenantSlug}_${spaceUri}`;
  }
}
